import { apiService } from '../../services/api';

/**
 * Returns a currently-valid access token, refreshing it silently if the one
 * in localStorage is expired or within the safety window of expiring.
 *
 * Why this exists: the axios interceptor in `src/services/api.ts` refreshes
 * tokens on 401 for axios-based calls. Other HTTP clients (notably SignalR's
 * internal `FetchHttpClient`) do not share that interceptor, so they keep
 * presenting stale tokens on every reconnect attempt and generate a 401 loop.
 * This helper gives those callers the same "transparent refresh" behaviour.
 *
 * Resolution order:
 *   1. No token in localStorage → return empty string; caller decides what to do.
 *   2. Token present + malformed (no parseable `exp` claim) → return as-is.
 *   3. Token present + not expiring soon → return as-is (happy path).
 *   4. Token present + expiring soon → attempt refresh via /auth/refresh. On
 *      success, update localStorage (via apiService.setTokens) and return the
 *      new access token. On any failure, return the stale token — the caller's
 *      subsequent 401 will surface the auth error through the normal channels.
 *
 * See AB#1470 for the bug this fixes.
 */

const EXPIRY_SAFETY_WINDOW_SECONDS = 30;

function getExpFromJwt(token: string): number | null {
    try {
        const payload = token.split('.')[1];
        if (!payload) return null;
        const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        return typeof decoded.exp === 'number' ? decoded.exp : null;
    } catch {
        return null;
    }
}

export async function ensureValidAccessToken(): Promise<string> {
    const token = localStorage.getItem('accessToken') || '';
    if (!token) return '';

    const exp = getExpFromJwt(token);
    if (exp == null) return token;

    const nowSec = Math.floor(Date.now() / 1000);
    if (exp - nowSec > EXPIRY_SAFETY_WINDOW_SECONDS) {
        return token;
    }

    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (!storedRefreshToken) return token;

    try {
        const res = await apiService.refreshAccessToken(storedRefreshToken);
        // Backend wraps the payload in { data: { accessToken, refreshToken } }.
        // Some call sites read `res.data` directly — support both shapes.
        const body = res.data as { accessToken?: string; refreshToken?: string; data?: { accessToken?: string; refreshToken?: string } };
        const newAccess = body?.accessToken ?? body?.data?.accessToken;
        const newRefresh = body?.refreshToken ?? body?.data?.refreshToken;
        if (newAccess && newRefresh) {
            apiService.setTokens(newAccess, newRefresh);
            return newAccess;
        }
        return token;
    } catch {
        return token;
    }
}
